import http from './axiosInstance'
import errorHandler from '../transformers/axiosErrors'
import { ApiResponse } from '@/types'
import Qs from 'qs'
import { Change, ChangeHistory } from '@/components/resume/v2/input/types'

interface ResumeDTO {
  parser?: string
  data?: any
  name?: string
  is_base?: boolean | 1 | 0
  original_file?: any
  parseOnUpload?: boolean | 1 | 0
}

export const downloadCv = async (
  resumeId: number,
  ops
): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.post(`/cvs/${resumeId}/download`, { ops })
    resp.data = response.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const updateResume = async (
  resumeId: number,
  data: ResumeDTO
): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.patch(`/resumes/${resumeId}`, data)
    resp.data = response.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const listResumes = async (options): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  const params = Qs.stringify(options, { arrayFormat: 'brackets' })
  try {
    const response = await http.get('/resumes', { params: options })
    resp.data = response.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const createResume = async (data: ResumeDTO): Promise<ApiResponse> => {
  const formData = new FormData()
  const { parseOnUpload, ...rest } = data

  Object.entries(rest).forEach(([key, value]) => {
    formData.append(key, value)
  })

  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.post('/resumes', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: parseOnUpload ? { parseOnUpload: true } : {},
    })
    resp.data = response.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const createManualResume = async (
  data: ResumeDTO
): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.post('/resumes', data)
    resp.data = response.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const deleteResume = async (resumeId: number): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.delete(`/resumes/${resumeId}`)
    resp.data = response.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const cloneResume = async (resumeId: number): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.get(`/resumes/${resumeId}/clone`)
    resp.data = response.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const createFromLinkedin = async (url): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.post('/resumes/create/linkedin', { url })
    resp.data = response.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const downloadResume = async (
  resumeId: number
): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.get(`/resumes/${resumeId}/download`)
    resp.data = response.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

interface ModelOptions {
  model: string
  temperature: number
  topP: number
}

interface ParseAndOptimizeResumeDTO {
  file: any
  jobDescription: string
  prompt: string
  modelOptions: ModelOptions
}

export const parseAndOptimizeResume = async (data: ParseAndOptimizeResumeDTO) => {
  const formData = new FormData()

  Object.entries(data).forEach(([key, value]) => {
    formData.append(key, value)
  })

  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.post('/resumes/parse-optimize', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    resp.data = response.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

interface ParseResumeDTO {
  file: any
  prompt: string
  modelOptions: ModelOptions
}

export const parseMarkdown = async (data: ParseResumeDTO) => {
  const formData = new FormData()

  Object.entries(data).forEach(([key, value]) => {
    formData.append(key, value)
  })

  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.post('/resumes/markdown', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    resp.data = response.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

interface OptimizeResumeDTO {
  prompt: string
  text: string
  jobDescription: string
  modelOptions: ModelOptions
}

export const optimizeResume = async (data: OptimizeResumeDTO) => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.post('/resumes/optimize', data, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    resp.data = response.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}


interface ResumeDataDTO {
  text: string
  suggestionsHistory: ChangeHistory[]
}

export const updateResumeData = async (
  resumeId: number,
  data: ResumeDataDTO
): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.patch(`/resumes/${resumeId}/data`, data)
    resp.data = response.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}
